import type { ItemUpgrade, StatUpgrade, Upgrade } from "../types"
import { UPGRADE_ICON_TEXTURE_KEYS } from "./icon-registry"

// ── STAT UPGRADES (level-up pool) ───────────────────────────────

export const STAT_UPGRADES: StatUpgrade[] = [
  {
    id: "hp-boost",
    offerSource: "level",
    type: "stat",
    name: "Hearty Meal",
    description: "Increase Max HP.",
    icon: "♥",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["hp-boost"],
    rarity: "common",
    statBoost: { maxHp: 1 },
    repeatable: true,
  },
  {
    id: "attack-speed-boost",
    offerSource: "level",
    type: "stat",
    name: "Tail Wag",
    description: "Increase Ball Speed.",
    icon: "⚡",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "common",
    statBoost: { attackSpeed: 0.1 },
    repeatable: true,
  },
  {
    id: "ball-bounces-boost",
    offerSource: "level",
    type: "stat",
    name: "Rubberized",
    description: "+1 Bounce.",
    icon: "◉",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["ball-bounces"],
    rarity: "common",
    statBoost: { ballBounces: 1 },
    repeatable: true,
  },
  {
    id: "ball-count-boost",
    offerSource: "level",
    type: "stat",
    name: "Ball Threat",
    description: "+1 Ball",
    icon: "◉◉",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["ball-bounces"],
    rarity: "common",
    statBoost: { ballCount: 1 },
    repeatable: true,
  },
  {
    id: "ball-size-boost",
    offerSource: "level",
    type: "stat",
    name: "Inferno Core",
    description: "Increase Ball Power.",
    icon: "●",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["ball-bounces"],
    rarity: "common",
    statBoost: { ballSize: 0.1 },
    repeatable: true,
  },
  {
    id: "flap-power-boost",
    offerSource: "level",
    type: "stat",
    name: "Wing Strength",
    description: "Increase Flap Power.",
    icon: "◆",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "common",
    statBoost: { flapPower: 0.1 },
    repeatable: true,
  },
]

// ── SHOP ITEMS ──────────────────────────────────────────────────

// ── Healing Items (4)
export const HEAL_ITEMS: ItemUpgrade[] = [
  {
    id: "item-kibble",
    offerSource: "shop",
    type: "item",
    name: "Kibble",
    description: "Restore 25 HP instantly.",
    icon: "🍖",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS.potion,
    rarity: "common",
    baseCost: 4,
    healAmount: 25,
    consumable: true,
    repeatable: true,
  },
  {
    id: "item-chicken-leg",
    offerSource: "shop",
    type: "item",
    name: "Chicken Leg",
    description: "Restore 50 HP instantly.",
    icon: "🍗",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["hi-potion"],
    rarity: "uncommon",
    baseCost: 8,
    healAmount: 50,
    consumable: true,
    repeatable: true,
  },
  {
    id: "item-full-feast",
    offerSource: "shop",
    type: "item",
    name: "Full Feast",
    description: "Restore 100 HP instantly.",
    icon: "🍽",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["hi-potion"],
    rarity: "rare",
    baseCost: 18,
    healAmount: 100,
    consumable: true,
    repeatable: true,
  },
  {
    id: "item-phoenix-feather",
    offerSource: "shop",
    type: "item",
    name: "Phoenix Feather",
    description: "Restore 50% Max HP instantly.",
    icon: "⚜",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["hi-potion"],
    rarity: "legendary",
    baseCost: 35,
    healFraction: 0.5,
    consumable: true,
    repeatable: true,
  },
]

// ── Bumper Effect Boosters
export const BUMPER_ITEMS: ItemUpgrade[] = [
  {
    id: "item-bank-boost",
    offerSource: "shop",
    type: "item",
    name: "Coin Node Magnet",
    description: "+30% chance for bank (yellow) bumpers.",
    icon: "🧲",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["pinball-router"],
    rarity: "common",
    baseCost: 7,
    bumperWeightBoost: { bank: 0.3 },
    repeatable: false,
  },
  // {
  //   id: "item-charge-boost",
  //   offerSource: "shop",
  //   type: "item",
  //   name: "Charge Amplifier",
  //   description: "+25% chance for charge (pink) bumpers.",
  //   icon: "✦",
  //   iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["pinball-router"],
  //   rarity: "uncommon",
  //   baseCost: 11,
  //   bumperWeightBoost: { charge: 0.25 },
  //   repeatable: false,
  // },
]

// ── Wave Safety & Guards (3)
export const GUARD_ITEMS: ItemUpgrade[] = [
  {
    id: "item-crash-helmet",
    offerSource: "shop",
    type: "item",
    name: "Crash Helmet",
    description: "+1 Wave Guard each wave.",
    icon: "⚔",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["crash-helmet"],
    rarity: "rare",
    baseCost: 15,
    passiveEffect: { waveGuardPerWave: 1 },
    repeatable: false,
  },
  {
    id: "item-shield-amulet",
    offerSource: "shop",
    type: "item",
    name: "Shield Amulet",
    description: "+2 Wave Guards each wave.",
    icon: "✦",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["crash-helmet"],
    rarity: "legendary",
    baseCost: 30,
    passiveEffect: { waveGuardPerWave: 2 },
    repeatable: false,
  },
]

// ── Roguelike Utility Items (Reroll, Revive, etc.)
export const UTILITY_ITEMS: ItemUpgrade[] = [
  {
    id: "item-dice-bag",
    offerSource: "shop",
    type: "item",
    name: "Dice Bag",
    description:
      "Grants one free reroll usable across any upgrade or shop phase.",
    icon: "🎲",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["pinball-router"],
    rarity: "common",
    baseCost: 15, // Increased cost as it is per run
    passiveEffect: { rerollCharges: 1 },
    consumable: false,
    repeatable: false,
  },
  {
    id: "item-phoenix-revival",
    offerSource: "shop",
    type: "item",
    name: "Phoenix Revival",
    description: "Revive with 25% Max HP after death. One use only.",
    icon: "🔥",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["hi-potion"],
    rarity: "rare",
    baseCost: 22,
    consumable: true,
    repeatable: false,
  },
  {
    id: "item-second-wind",
    offerSource: "shop",
    type: "item",
    name: "Second Wind",
    description: "Revive with 40% Max HP after death by boss. One use only.",
    icon: "💨",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["hi-potion"],
    rarity: "legendary",
    baseCost: 40,
    consumable: true,
    repeatable: false,
  },
]

// ── Roguelike Relics & Artifacts (Passive Effects)
export const RELIC_ITEMS: ItemUpgrade[] = [
  {
    id: "item-glass-cannon",
    offerSource: "shop",
    type: "item",
    name: "Glass Cannon",
    description: "+50% damage but -30% max HP.",
    icon: "⚡",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "rare",
    baseCost: 14,
    passiveEffect: { damageMultiplier: 0.5, hpMultiplier: -0.3 },
    repeatable: false,
  },
  {
    id: "item-berserker-rage",
    offerSource: "shop",
    type: "item",
    name: "Berserker Rage",
    description: "+100% ball speed but -25% max HP.",
    icon: "🔴",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "rare",
    baseCost: 16,
    passiveEffect: { speedMultiplier: 1.0, hpMultiplier: -0.25 },
    repeatable: false,
  },
  {
    id: "item-reinforced-armor",
    offerSource: "shop",
    type: "item",
    name: "Reinforced Armor",
    description: "+40% max HP but -20% ball speed.",
    icon: "🛡",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["hp-boost"],
    rarity: "uncommon",
    baseCost: 10,
    passiveEffect: { hpMultiplier: 0.4, speedMultiplier: -0.2 },
    repeatable: false,
  },
  {
    id: "item-lucky-dice",
    offerSource: "shop",
    type: "item",
    name: "Lucky Dice",
    description: "+15% chance for rare bumpers.",
    icon: "🎯",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["pinball-router"],
    rarity: "uncommon",
    baseCost: 8,
    bumperWeightBoost: { charge: 0.1, bank: 0.1 },
    repeatable: false,
  },
]

// ── Wave Modifiers & Risk/Reward Items
export const MODIFIER_ITEMS: ItemUpgrade[] = [
  {
    id: "item-cursed-ring",
    offerSource: "shop",
    type: "item",
    name: "Cursed Ring",
    description: "+2 free waves. Enemies gain +25% HP.",
    icon: "💍",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["crash-helmet"],
    rarity: "rare",
    baseCost: 20,
    passiveEffect: { wavesSkipped: 2 },
    repeatable: false,
  },
  {
    id: "item-tempo-boost",
    offerSource: "shop",
    type: "item",
    name: "Tempo Boost",
    description: "Gain +1 level immediately.",
    icon: "⬆",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "uncommon",
    baseCost: 12,
    consumable: true,
    repeatable: true,
  },
  {
    id: "item-momentum",
    offerSource: "shop",
    type: "item",
    name: "Momentum",
    description: "Gain +2 levels immediately.",
    icon: "⬆⬆",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "rare",
    baseCost: 25,
    consumable: true,
    repeatable: false,
  },
]

// ── Economy & Multiplier Items
export const ECONOMY_ITEMS: ItemUpgrade[] = [
  {
    id: "item-gold-tooth",
    offerSource: "shop",
    type: "item",
    name: "Gold Tooth",
    description: "+10% coins earned from enemies.",
    icon: "💰",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["pinball-router"],
    rarity: "uncommon",
    baseCost: 9,
    passiveEffect: { coinMultiplier: 0.1 },
    repeatable: false,
  },
  {
    id: "item-treasure-map",
    offerSource: "shop",
    type: "item",
    name: "Treasure Map",
    description: "+20% coins earned from enemies.",
    icon: "🗺",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["pinball-router"],
    rarity: "rare",
    baseCost: 18,
    passiveEffect: { coinMultiplier: 0.2 },
    repeatable: false,
  },
  {
    id: "item-exp-crystal",
    offerSource: "shop",
    type: "item",
    name: "Experience Crystal",
    description: "+15% exp earned from enemies.",
    icon: "🔷",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "uncommon",
    baseCost: 10,
    passiveEffect: { expMultiplier: 0.15 },
    repeatable: false,
  },
  {
    id: "item-scholars-tome",
    offerSource: "shop",
    type: "item",
    name: "Scholar's Tome",
    description: "+30% exp earned from enemies.",
    icon: "📖",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["attack-speed"],
    rarity: "rare",
    baseCost: 20,
    passiveEffect: { expMultiplier: 0.3 },
    repeatable: false,
  },
]

// ── Combo & Scoring Items
export const COMBO_ITEMS: ItemUpgrade[] = [
  {
    id: "item-combo-master",
    offerSource: "shop",
    type: "item",
    name: "Combo Master",
    description: "Combo meter fills 15% faster.",
    icon: "⚙",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["pinball-router"],
    rarity: "uncommon",
    baseCost: 11,
    passiveEffect: { combFillSpeedMultiplier: 0.15 },
    repeatable: false,
  },
  {
    id: "item-score-boost",
    offerSource: "shop",
    type: "item",
    name: "Score Multiplier",
    description: "+20% bonus to all scores.",
    icon: "⭐",
    iconTextureKey: UPGRADE_ICON_TEXTURE_KEYS["crash-helmet"],
    rarity: "rare",
    baseCost: 19,
    passiveEffect: { scoreMultiplier: 0.2 },
    repeatable: false,
  },
]

// ── Combined arrays ─────────────────────────────────────────────

export const UPGRADES: Upgrade[] = [...STAT_UPGRADES]

export const SHOP_UPGRADES: Upgrade[] = [
  ...HEAL_ITEMS,
  ...BUMPER_ITEMS,
  ...GUARD_ITEMS,
  ...UTILITY_ITEMS,
  ...RELIC_ITEMS,
  ...MODIFIER_ITEMS,
  ...ECONOMY_ITEMS,
  ...COMBO_ITEMS,
]
